import * as XLSX from "xlsx";

export const BULK_RESULT_STORAGE_KEY = "greenCoffeeBulkImportResult";

export const BULK_RECORD_STATUS = {
  SUCCESS: "SUCCESS",
  FAILED: "FAILED",
  /** Imported with data discrepancies (shown under Data Discrepancies tab). */
  DATA_DISCREPANCY: "DATA_DISCREPANCY",
};

/** @deprecated alias — same as DATA_DISCREPANCY */
BULK_RECORD_STATUS.ISSUES_DETECTED = BULK_RECORD_STATUS.DATA_DISCREPANCY;

export function isDataDiscrepancyRecord(record) {
  const status = String(record?.status || "").toUpperCase();
  return (
    status === BULK_RECORD_STATUS.DATA_DISCREPANCY || status === "ISSUES_DETECTED"
  );
}

export function isSuccessRecord(record) {
  return String(record?.status || "").toUpperCase() === BULK_RECORD_STATUS.SUCCESS;
}

export function isFailedRecord(record) {
  return String(record?.status || "").toUpperCase() === BULK_RECORD_STATUS.FAILED;
}

/** Warning / data-discrepancy types shown on the bulk import result page. */
export const BULK_ISSUE_TYPE = {
  OUTTURN_NUMBER_MISMATCH: "OUTTURN_NUMBER_MISMATCH",
  DUPLICATE_OUTTURN_NUMBER: "DUPLICATE_OUTTURN_NUMBER",
  MISSING_OUTTURN_NUMBER: "MISSING_OUTTURN_NUMBER",
  GROWER_IDENTITY_MISMATCH: "GROWER_IDENTITY_MISMATCH",
  MISSING_GROWER_CREDENTIALS: "MISSING_GROWER_CREDENTIALS",
  INVALID_DATA: "INVALID_DATA",
};

export const BULK_ISSUE_I18N_KEYS = {
  [BULK_ISSUE_TYPE.INVALID_DATA]: {
    title: "issueInvalidDataTitle",
    description: "issueInvalidDataDescription",
  },
  [BULK_ISSUE_TYPE.OUTTURN_NUMBER_MISMATCH]: {
    title: "issueOutturnNumberMismatchTitle",
    description: "issueOutturnNumberMismatchDescription",
  },
  [BULK_ISSUE_TYPE.DUPLICATE_OUTTURN_NUMBER]: {
    title: "issueDuplicateOutturnNumberTitle",
    description: "issueDuplicateOutturnNumberDescription",
  },
  [BULK_ISSUE_TYPE.MISSING_OUTTURN_NUMBER]: {
    title: "issueMissingOutturnNumberTitle",
    description: "issueMissingOutturnNumberDescription",
  },
  [BULK_ISSUE_TYPE.GROWER_IDENTITY_MISMATCH]: {
    title: "issueGrowerIdentityMismatchTitle",
    description: "issueGrowerIdentityMismatchDescription",
  },
  [BULK_ISSUE_TYPE.MISSING_GROWER_CREDENTIALS]: {
    title: "issueMissingGrowerCredentialsTitle",
    description: "issueMissingGrowerCredentialsDescription",
  },
};

const OUTTURN_RELATED_ISSUE_TYPES = new Set([
  BULK_ISSUE_TYPE.OUTTURN_NUMBER_MISMATCH,
  BULK_ISSUE_TYPE.DUPLICATE_OUTTURN_NUMBER,
  BULK_ISSUE_TYPE.MISSING_OUTTURN_NUMBER,
]);

const GROWER_RELATED_ISSUE_TYPES = new Set([
  BULK_ISSUE_TYPE.GROWER_IDENTITY_MISMATCH,
  BULK_ISSUE_TYPE.MISSING_GROWER_CREDENTIALS,
]);

export function isOutturnRelatedIssue(issueType) {
  return OUTTURN_RELATED_ISSUE_TYPES.has(issueType);
}

export function isGrowerRelatedIssue(issueType) {
  return GROWER_RELATED_ISSUE_TYPES.has(issueType);
}

export function resolveBulkIssueType(record) {
  if (record?.issueType && BULK_ISSUE_I18N_KEYS[record.issueType]) {
    return record.issueType;
  }
  if (record?.hasOutturnDiscrepancy) {
    return BULK_ISSUE_TYPE.OUTTURN_NUMBER_MISMATCH;
  }
  return BULK_ISSUE_TYPE.OUTTURN_NUMBER_MISMATCH;
}

/** Exact column headers from Milling bulk import template */
export const MILLING_BULK_IMPORT_TEMPLATE_HEADERS = [
  "ID",
  "Outturn No",
  "Grower",
  "Input Weight With Bags (Kgs)",
  "Grower Name",
  "Coffee Type",
  "Clean Coffee (Milling Output Details)",
  "Bags (Milling Output Details)",
  "Pockets (Milling Output Details)",
  "Weight (Milling Output Details)",
  "Bulk Outturn (Milling Output Details)",
  "Ratio (Milling Output Details)",
];

const REQUIRED_CANONICAL_KEYS = ["outturnno", "inputweightwithbagskgs"];

const HEADER_ALIASES = {
  id: ["id"],
  outturnno: ["outturn no", "outturnno", "outturn number", "outturnnumber", "outturn"],
  grower: ["grower"],
  inputweightwithbagskgs: [
    "input weight with bags kgs",
    "inputweightwithbagskgs",
    "input weight with bags",
  ],
  growername: ["grower name", "growername"],
  coffeetype: ["coffee type", "coffeetype"],
  cleancoffee: [
    "clean coffee milling output details",
    "cleancoffee",
    "clean coffee",
  ],
  bagsmillingoutput: ["bags milling output details", "bagsmillingoutput"],
  pocketsmillingoutput: ["pockets milling output details", "pocketsmillingoutput"],
  weightmillingoutput: [
    "weight milling output details",
    "weightmillingoutput",
    "net weight",
    "netweight",
  ],
  bulkoutturnmillingoutput: [
    "bulk outturn milling output details",
    "bulkoutturnmillingoutput",
  ],
  ratiomillingoutput: ["ratio milling output details", "ratiomillingoutput"],
};

function cleanHeaderLabel(value) {
  return String(value ?? "")
    .replace(/^\ufeff/, "")
    .trim();
}

function normalizeHeaderKey(value) {
  return cleanHeaderLabel(value).toLowerCase().replace(/[^a-z0-9]/g, "");
}

function getExpectedHeaderNorms() {
  return MILLING_BULK_IMPORT_TEMPLATE_HEADERS.map(normalizeHeaderKey);
}

const MIN_HEADER_MATCH_COUNT = 8;

function scoreHeaderRow(row) {
  const expected = getExpectedHeaderNorms();
  const norms = (row || []).map((c) => normalizeHeaderKey(c)).filter(Boolean);
  const matched = expected.filter((h) => norms.includes(h));
  return { score: matched.length, norms, matched };
}

function findHeaderRowIndex(rowsAoa, maxScan = 25) {
  let bestIndex = -1;
  let bestScore = 0;
  for (let i = 0; i < Math.min(rowsAoa.length, maxScan); i++) {
    const { score } = scoreHeaderRow(rowsAoa[i]);
    if (score > bestScore) {
      bestScore = score;
      bestIndex = i;
    }
  }
  if (bestIndex < 0 || bestScore < MIN_HEADER_MATCH_COUNT) {
    return null;
  }
  return { index: bestIndex, score: bestScore };
}

function sheetAoaToRowObjects(sheet) {
  const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
  const headerInfo = findHeaderRowIndex(aoa);
  if (!headerInfo) {
    return { rawHeaders: [], rows: [], headerRowIndex: -1, headerMatchScore: 0 };
  }

  const headerRow = aoa[headerInfo.index] || [];
  const headerLabels = headerRow.map((cell, idx) => {
    const label = cleanHeaderLabel(cell);
    return label || `__col_${idx}`;
  });
  const rawHeaders = headerLabels.filter((h) => !h.startsWith("__col_"));

  const rows = [];
  for (let r = headerInfo.index + 1; r < aoa.length; r++) {
    const line = aoa[r] || [];
    if (!line.some((c) => String(c ?? "").trim() !== "")) continue;
    const obj = {};
    headerLabels.forEach((label, colIdx) => {
      obj[label] = line[colIdx] ?? "";
    });
    rows.push(obj);
  }

  return {
    rawHeaders,
    rows,
    headerRowIndex: headerInfo.index,
    headerMatchScore: headerInfo.score,
  };
}

function pickSheetWithTemplate(workbook) {
  let best = null;
  for (const sheetName of workbook.SheetNames) {
    const sheet = workbook.Sheets[sheetName];
    if (!sheet) continue;
    const parsed = sheetAoaToRowObjects(sheet);
    if (parsed.headerRowIndex < 0) continue;
    if (!best || parsed.headerMatchScore > best.headerMatchScore) {
      best = { sheetName, ...parsed };
    }
  }
  return best;
}

function buildTemplateErrorMessage(missingNorms) {
  const missingLabels = missingNorms
    .map((key) => {
      const idx = MILLING_BULK_IMPORT_TEMPLATE_HEADERS.findIndex(
        (h) => normalizeHeaderKey(h) === key
      );
      return idx >= 0 ? MILLING_BULK_IMPORT_TEMPLATE_HEADERS[idx] : key;
    })
    .filter(Boolean);

  if (missingLabels.length >= 6) {
    return {
      short:
        "Invalid template. The header row does not match the import template. Download the XLS template and try again.",
      full: `Invalid template. Missing or mismatched columns: ${missingLabels.join(", ")}`,
    };
  }

  const joined = missingLabels.join(", ");
  return {
    short: `Invalid template. Missing or mismatched columns: ${joined}`,
    full: `Invalid template. Missing or mismatched columns: ${joined}`,
  };
}

function mapHeaderToCanonical(headerKey) {
  const norm = normalizeHeaderKey(headerKey);
  for (const [canonical, aliases] of Object.entries(HEADER_ALIASES)) {
    if (aliases.some((a) => normalizeHeaderKey(a) === norm)) {
      return canonical;
    }
  }
  return norm;
}

function mapRow(row) {
  const mapped = {};
  Object.keys(row || {}).forEach((key) => {
    const canonical = mapHeaderToCanonical(key);
    mapped[canonical] = row[key];
  });
  return mapped;
}

function hasMillingOutputLine(row) {
  return (
    String(row.cleancoffee || "").trim() !== "" ||
    String(row.weightmillingoutput ?? "").trim() !== "" ||
    String(row.bagsmillingoutput ?? "").trim() !== "" ||
    String(row.pocketsmillingoutput ?? "").trim() !== ""
  );
}

function millingOutputFromRow(row) {
  return {
    cleanCoffee: row.cleancoffee != null ? String(row.cleancoffee).trim() : "",
    bags: row.bagsmillingoutput,
    pockets: row.pocketsmillingoutput,
    weight: row.weightmillingoutput,
    bulkOutturn: row.bulkoutturnmillingoutput != null ? String(row.bulkoutturnmillingoutput).trim() : "",
    ratio: row.ratiomillingoutput,
  };
}

function isBatchHeaderRow(row) {
  return (
    String(row.outturnno || "").trim() !== "" ||
    String(row.id || "").trim() !== ""
  );
}

function rowHasData(row) {
  return isBatchHeaderRow(row) || hasMillingOutputLine(row);
}

/**
 * Milling sheets use merged-style rows: batch fields on the first line,
 * additional milling output lines leave batch columns empty.
 */
export function groupMillingRows(mappedRows) {
  const batches = [];
  let current = null;

  mappedRows.forEach((row) => {
    if (!rowHasData(row)) return;

    if (isBatchHeaderRow(row)) {
      if (current) batches.push(current);
      current = {
        id: row.id != null ? String(row.id).trim() : "",
        outturnno: row.outturnno != null ? String(row.outturnno).trim() : "",
        grower: row.grower != null ? String(row.grower).trim() : "",
        inputweightwithbagskgs: row.inputweightwithbagskgs,
        growername: row.growername != null ? String(row.growername).trim() : "",
        coffeetype: row.coffeetype != null ? String(row.coffeetype).trim() : "",
        millingOutputs: [],
      };
      if (hasMillingOutputLine(row)) {
        current.millingOutputs.push(millingOutputFromRow(row));
      }
      return;
    }

    if (current && hasMillingOutputLine(row)) {
      current.millingOutputs.push(millingOutputFromRow(row));
    }
  });

  if (current) batches.push(current);
  return batches;
}

function sumOutputWeights(outputs) {
  return (outputs || []).reduce((sum, line) => {
    const w = Number(line.weight);
    return Number.isFinite(w) ? sum + w : sum;
  }, 0);
}

function validateTemplateHeaders(sheetHeaders) {
  const normalizedSheet = new Set(sheetHeaders.map(normalizeHeaderKey));
  const expected = MILLING_BULK_IMPORT_TEMPLATE_HEADERS.map(normalizeHeaderKey);
  const missing = expected.filter((h) => !normalizedSheet.has(h));
  return { valid: missing.length === 0, missing };
}

export function parseGreenCoffeeBulkFile(arrayBuffer) {
  const workbook = XLSX.read(arrayBuffer, { type: "array", cellDates: true });
  if (!workbook.SheetNames?.length) {
    return { templateError: true, message: "No worksheet found in the file.", headers: [], rows: [] };
  }

  const sheetData = pickSheetWithTemplate(workbook);
  if (!sheetData || sheetData.headerRowIndex < 0) {
    return {
      templateError: true,
      message:
        "Could not find a valid header row. Use the provided XLS template and ensure column headers are on the first data row.",
      messageFull:
        "Could not find a valid header row. Use the provided XLS template and ensure column headers are on the first data row.",
      headers: [],
      rows: [],
    };
  }

  const { rawHeaders, rows: json } = sheetData;
  if (!json.length) {
    return {
      templateError: true,
      message: "The uploaded file is empty.",
      messageFull: "The uploaded file is empty.",
      headers: [],
      rows: [],
    };
  }

  const templateCheck = validateTemplateHeaders(rawHeaders);
  if (!templateCheck.valid) {
    const { short, full } = buildTemplateErrorMessage(templateCheck.missing);
    return {
      templateError: true,
      message: short,
      messageFull: full,
      headers: rawHeaders.map(normalizeHeaderKey),
      rows: [],
    };
  }

  const mappedHeaders = rawHeaders.map((h) => mapHeaderToCanonical(h));
  const missingRequired = REQUIRED_CANONICAL_KEYS.filter((k) => !mappedHeaders.includes(k));
  if (missingRequired.length) {
    const msg = `Invalid template. Required columns missing: ${missingRequired.join(", ")}`;
    return {
      templateError: true,
      message: msg,
      messageFull: msg,
      headers: mappedHeaders,
      rows: [],
    };
  }

  const flatRows = json.map(mapRow).filter(rowHasData);
  const rows = groupMillingRows(flatRows);

  if (!rows.length) {
    return {
      templateError: true,
      message: "No data rows found in the file.",
      headers: mappedHeaders,
      rows: [],
    };
  }

  return { templateError: false, headers: mappedHeaders, rows };
}

function buildPayloadFromBatch(batch) {
  const outturnNumber = String(batch.outturnno || "").trim();
  const inputWeight = Number(batch.inputweightwithbagskgs);
  const outputWeightSum = sumOutputWeights(batch.millingOutputs);
  const totalGreenBeans =
    Number.isFinite(inputWeight) && inputWeight > 0 ? inputWeight : outputWeightSum;

  return {
    id: batch.id,
    outturnNumber,
    grower: batch.grower,
    growerName: batch.growername,
    coffeeType: batch.coffeetype,
    inputWeightWithBags: batch.inputweightwithbagskgs,
    totalGreenBeans,
    netWeight: totalGreenBeans,
    millingOutputs: batch.millingOutputs || [],
  };
}

/**
 * Client-side mock import — validates parsed batches and returns dummy result payload.
 */
function createDiscrepancyRecord({ id, payloadJsonData, issueType, issueContext, dummyId }) {
  const hasOutturnDiscrepancy = isOutturnRelatedIssue(issueType);
  return {
    id,
    status: BULK_RECORD_STATUS.DATA_DISCREPANCY,
    issueType,
    issueContext: issueContext || {},
    message: "",
    hasOutturnDiscrepancy,
    payloadJsonData,
    recordedJsonData: {
      id: dummyId,
      outturnNumber: payloadJsonData.outturnNumber || "",
      greenBeansId: `GB-${dummyId}-0`,
    },
  };
}

function detectDummyIssue({ outturnNumber, outturnKey, grower, growerName, index, isDuplicateOutturn }) {
  if (!outturnNumber) {
    return { issueType: BULK_ISSUE_TYPE.MISSING_OUTTURN_NUMBER };
  }
  if (isDuplicateOutturn) {
    return { issueType: BULK_ISSUE_TYPE.DUPLICATE_OUTTURN_NUMBER };
  }
  if (!grower && !growerName) {
    return { issueType: BULK_ISSUE_TYPE.MISSING_GROWER_CREDENTIALS };
  }
  if (outturnKey.includes("grower-mismatch") || outturnKey.includes("-gm-")) {
    return {
      issueType: BULK_ISSUE_TYPE.GROWER_IDENTITY_MISMATCH,
      issueContext: {
        mobileGrowerName: "James Ochieng",
        xlsGrowerName: growerName || grower || "Uploaded Grower",
      },
    };
  }
  if (outturnKey.includes("0026") || outturnKey.includes("0012") || index % 4 === 0) {
    return { issueType: BULK_ISSUE_TYPE.OUTTURN_NUMBER_MISMATCH };
  }
  return null;
}

const SYNTHETIC_ISSUE_SAMPLES = {
  [BULK_ISSUE_TYPE.OUTTURN_NUMBER_MISMATCH]: {
    payloadJsonData: {
      id: "DEMO-OT-MISMATCH",
      outturnNumber: "DEMO-OT-MISMATCH",
      grower: "GR-201",
      growerName: "Demo Grower A",
      coffeeType: "Arabica",
      totalGreenBeans: 420,
    },
    issueContext: {},
  },
  [BULK_ISSUE_TYPE.DUPLICATE_OUTTURN_NUMBER]: {
    payloadJsonData: {
      id: "DEMO-OT-DUP",
      outturnNumber: "DEMO-OT-DUPLICATE",
      grower: "GR-202",
      growerName: "Demo Grower B",
      coffeeType: "Robusta",
      totalGreenBeans: 380,
    },
    issueContext: {},
  },
  [BULK_ISSUE_TYPE.MISSING_OUTTURN_NUMBER]: {
    payloadJsonData: {
      id: "DEMO-OT-MISSING",
      outturnNumber: "",
      grower: "GR-203",
      growerName: "Demo Grower C",
      coffeeType: "Arabica",
      totalGreenBeans: 310,
    },
    issueContext: {},
  },
  [BULK_ISSUE_TYPE.GROWER_IDENTITY_MISMATCH]: {
    payloadJsonData: {
      id: "DEMO-GROWER-MISMATCH",
      outturnNumber: "DEMO-GROWER-MISMATCH",
      grower: "GR-999",
      growerName: "Jane Wanjiku",
      coffeeType: "Arabica",
      totalGreenBeans: 450,
    },
    issueContext: {
      mobileGrowerName: "John Kamau",
      xlsGrowerName: "Jane Wanjiku",
    },
  },
  [BULK_ISSUE_TYPE.MISSING_GROWER_CREDENTIALS]: {
    payloadJsonData: {
      id: "DEMO-NO-GROWER",
      outturnNumber: "DEMO-NO-GROWER",
      grower: "",
      growerName: "",
      coffeeType: "Arabica",
      totalGreenBeans: 290,
    },
    issueContext: {},
  },
};

function ensureAllIssueTypesInRecords(records, startDummyId) {
  let dummyId = startDummyId;
  let syntheticId = 90000;
  const presentTypes = new Set(
    records
      .filter((r) => isDataDiscrepancyRecord(r))
      .map((r) => resolveBulkIssueType(r))
  );

  Object.values(BULK_ISSUE_TYPE).forEach((issueType) => {
    if (presentTypes.has(issueType)) return;
    const sample = SYNTHETIC_ISSUE_SAMPLES[issueType];
    if (!sample) return;
    dummyId += 1;
    syntheticId += 1;
    records.push(
      createDiscrepancyRecord({
        id: syntheticId,
        payloadJsonData: { ...sample.payloadJsonData },
        issueType,
        issueContext: sample.issueContext ? { ...sample.issueContext } : {},
        dummyId,
      })
    );
  });
}

export function buildDummyBulkImportResult(batches) {
  const records = [];
  const outturnCounts = new Map();
  let successCount = 0;
  let failedCount = 0;
  let dummyId = 9000;

  batches.forEach((batch) => {
    const outturnKey = String(batch.outturnno || "").trim().toLowerCase();
    if (!outturnKey) return;
    outturnCounts.set(outturnKey, (outturnCounts.get(outturnKey) || 0) + 1);
  });

  const seenOutturns = new Set();

  batches.forEach((batch, index) => {
    const payloadJsonData = buildPayloadFromBatch(batch);
    const outturnNumber = String(payloadJsonData.outturnNumber || "").trim();
    const outturnKey = outturnNumber.toLowerCase();
    const grower = String(payloadJsonData.grower || "").trim();
    const growerName = String(payloadJsonData.growerName || "").trim();
    const totalGreenBeans = Number(payloadJsonData.totalGreenBeans);
    const isDuplicateOutturn =
      outturnKey.length > 0 &&
      ((outturnCounts.get(outturnKey) || 0) > 1 && seenOutturns.has(outturnKey));

    if (outturnKey && !isDuplicateOutturn) {
      seenOutturns.add(outturnKey);
    }

    if (outturnNumber && outturnNumber.toLowerCase().includes("fail")) {
      records.push({
        id: index + 1,
        status: BULK_RECORD_STATUS.FAILED,
        payloadJsonData,
        message: "Simulated import failure (dummy validation)",
        hasOutturnDiscrepancy: false,
      });
      failedCount += 1;
      return;
    }

    if (
      outturnNumber &&
      (!Number.isFinite(totalGreenBeans) || totalGreenBeans <= 0)
    ) {
      records.push({
        id: index + 1,
        status: BULK_RECORD_STATUS.FAILED,
        payloadJsonData,
        message: "Input Weight With Bags (Kgs) or milling output weight is required",
        hasOutturnDiscrepancy: false,
      });
      failedCount += 1;
      return;
    }

    const issue = detectDummyIssue({
      outturnNumber,
      outturnKey,
      grower,
      growerName,
      index,
      isDuplicateOutturn,
    });

    if (issue) {
      dummyId += 1;
      records.push(
        createDiscrepancyRecord({
          id: index + 1,
          payloadJsonData,
          issueType: issue.issueType,
          issueContext: issue.issueContext,
          dummyId,
        })
      );
      return;
    }

    dummyId += 1;
    records.push({
      id: index + 1,
      status: BULK_RECORD_STATUS.SUCCESS,
      payloadJsonData,
      message: "",
      hasOutturnDiscrepancy: false,
      recordedJsonData: {
        id: dummyId,
        outturnNumber,
        greenBeansId: `GB-${dummyId}-0`,
      },
    });
    successCount += 1;
  });

  ensureAllIssueTypesInRecords(records, dummyId);

  const discrepancyCount = records.filter((r) => isDataDiscrepancyRecord(r)).length;
  const importedRecordsCount =
    records.filter((r) => isSuccessRecord(r) || isDataDiscrepancyRecord(r)).length;

  return {
    id: Date.now(),
    totalRecordsCount: records.length,
    successRecordsCount: successCount,
    failedRecordsCount: failedCount,
    discrepancyRecordsCount: discrepancyCount,
    importedRecordsCount,
    records,
    templateError: false,
  };
}

export async function processGreenCoffeeBulkFile(file) {
  const buffer = await file.arrayBuffer();
  const parsed = parseGreenCoffeeBulkFile(buffer);

  if (parsed.templateError) {
    return {
      ok: false,
      templateError: true,
      message: parsed.message,
      messageFull: parsed.messageFull || parsed.message,
      data: {
        templateError: true,
        totalRecordsCount: 0,
        failedRecordsCount: 0,
        records: [],
        message: parsed.message,
        messageFull: parsed.messageFull || parsed.message,
      },
    };
  }

  const data = buildDummyBulkImportResult(parsed.rows);
  const ok = data.totalRecordsCount > 0;

  return {
    ok,
    templateError: false,
    data,
  };
}

// ──────────────────────────────────────────────────────────────────────────
//  Backend integration — adapts API records to the view-model the existing
//  components consume, so the UI does not need to change.
// ──────────────────────────────────────────────────────────────────────────

/** Record status values returned by the API. */
export const SERVER_RECORD_STATUS = {
  SUCCESS: "SUCCESS",
  ERROR_DETECTED: "ERROR_DETECTED",
  DELETED: "DELETED",
};

/** Bulk import status values returned by the API. */
export const SERVER_BULK_IMPORT_STATUS = {
  VALIDATING: "VALIDATING",
  SUCCESS: "SUCCESS",
  CANCELLED: "CANCELLED",
};

/** API error code → existing front-end issue type. */
export const SERVER_ERROR_CODE_TO_ISSUE_TYPE = {
  OUTTURN_MISMATCH: BULK_ISSUE_TYPE.OUTTURN_NUMBER_MISMATCH,
  OUTTURN_MISSING: BULK_ISSUE_TYPE.MISSING_OUTTURN_NUMBER,
  OUTTURN_DUPLICATE: BULK_ISSUE_TYPE.DUPLICATE_OUTTURN_NUMBER,
  GROWER_IDENTITY_MISMATCH: BULK_ISSUE_TYPE.GROWER_IDENTITY_MISMATCH,
  GROWER_IDENTITY_MISSING: BULK_ISSUE_TYPE.MISSING_GROWER_CREDENTIALS,
  INVALID_DATA: BULK_ISSUE_TYPE.INVALID_DATA,
};

/** API error code → i18n key (param-free friendly labels) for tooltips. */
export const SERVER_ERROR_CODE_I18N = {
  OUTTURN_MISMATCH: "errorCodeOutturnMismatch",
  OUTTURN_MISSING: "errorCodeOutturnMissing",
  OUTTURN_DUPLICATE: "errorCodeOutturnDuplicate",
  GROWER_IDENTITY_MISMATCH: "errorCodeGrowerMismatch",
  GROWER_IDENTITY_MISSING: "errorCodeGrowerMissing",
  INVALID_DATA: "errorCodeInvalidData",
};

/** API error code → i18n key for short labels (filter chips). */
export const SERVER_ERROR_CODE_LABEL_I18N = {
  OUTTURN_MISMATCH: "chipOutturnMismatch",
  OUTTURN_MISSING: "chipOutturnMissing",
  OUTTURN_DUPLICATE: "chipOutturnDuplicate",
  GROWER_IDENTITY_MISMATCH: "chipGrowerMismatch",
  GROWER_IDENTITY_MISSING: "chipGrowerMissing",
  INVALID_DATA: "chipInvalidData",
};

/** Pick the primary issue type for a record from its API error codes. */
export function mapServerErrorCodesToIssueType(errorCodes = []) {
  for (const code of errorCodes) {
    if (SERVER_ERROR_CODE_TO_ISSUE_TYPE[code]) {
      return SERVER_ERROR_CODE_TO_ISSUE_TYPE[code];
    }
  }
  return BULK_ISSUE_TYPE.OUTTURN_NUMBER_MISMATCH;
}

/**
 * Map one API import record to the shape the result table/components expect.
 * Keeps the raw API fields (errorCodes/errorMetadata/recordedJsonData) for
 * field-level highlighting and editing.
 */
export function mapServerRecord(record) {
  const rec = record.recordedJsonData || {};
  const errorCodes = record.errorCodes || [];
  const isError = String(record.status).toUpperCase() === SERVER_RECORD_STATUS.ERROR_DETECTED;
  const status = isError
    ? BULK_RECORD_STATUS.DATA_DISCREPANCY
    : BULK_RECORD_STATUS.SUCCESS;

  return {
    id: record.id,
    serverStatus: record.status,
    status,
    errorCodes,
    errorMetadata: record.errorMetadata || {},
    issueType: mapServerErrorCodesToIssueType(errorCodes),
    issueContext: {
      xlsGrowerName: rec.growerName || rec.growerCode || "",
    },
    hasOutturnDiscrepancy: Boolean(record.errorMetadata && record.errorMetadata.outturnNumber),
    payloadJsonData: {
      id: rec.inputLotId != null ? rec.inputLotId : "",
      outturnNumber: rec.outturnNumber != null ? rec.outturnNumber : "",
      grower: rec.growerCode != null ? rec.growerCode : "",
      growerName: rec.growerName != null ? rec.growerName : "",
      coffeeType: rec.coffeeType != null ? rec.coffeeType : "",
      totalGreenBeans: rec.greenBeansTotal != null ? rec.greenBeansTotal : "",
    },
    recordedJsonData: rec,
    rawPayload: record.payloadJsonData || null,
    parchmentCoffeeId: record.parchmentCoffeeId || null,
  };
}

/**
 * Map the full validate / get-by-id API payload to the detail object used by
 * the result page (and stored in sessionStorage between routes).
 */
export function mapServerImport(data = {}) {
  const records = (data.records || []).map(mapServerRecord);
  const successRecordsCount = records.filter(isSuccessRecord).length;
  const discrepancyRecordsCount = records.filter(isDataDiscrepancyRecord).length;
  return {
    bulkImportId: data.bulkImportId != null ? data.bulkImportId : data.id,
    status: data.status || null,
    originalFileName: data.originalFileName || null,
    downloadUrl: data.downloadUrl || null,
    totalRecordsCount:
      data.totalRecordsCount != null ? data.totalRecordsCount : records.length,
    errorRecordsCount:
      data.errorRecordsCount != null ? data.errorRecordsCount : discrepancyRecordsCount,
    successRecordsCount,
    failedRecordsCount: 0,
    discrepancyRecordsCount,
    // Only valid (SUCCESS) rows are imported.
    importedRecordsCount: successRecordsCount,
    records,
    templateError: false,
  };
}

/** Build the field-level payload sent to the update-record endpoint. */
export function buildRecordUpdatePayload(record, overrides = {}) {
  const rec = record.recordedJsonData || {};
  return {
    inputLotId: rec.inputLotId != null ? rec.inputLotId : null,
    outturnNumber: rec.outturnNumber != null ? rec.outturnNumber : null,
    growerCode: rec.growerCode != null ? rec.growerCode : null,
    growerName: rec.growerName != null ? rec.growerName : null,
    coffeeType: rec.coffeeType != null ? rec.coffeeType : null,
    greenBeansTotal: rec.greenBeansTotal != null ? rec.greenBeansTotal : null,
    // Quality gradings are preserved as-is — the edit form only fixes the
    // header-level fields that drive validation errors.
    qualityGradings: rec.qualityGradings || [],
    ...overrides,
  };
}

